import type { Locale } from '../lib/sceneStore'

export type LocalizedText = Readonly<Record<Locale, string>>

export type ActId =
  | 'act-00'
  | 'act-01'
  | 'act-02'
  | 'act-03'
  | 'act-04'
  | 'act-05'
  | 'act-06'
  | 'act-07'
  | 'act-08'

export interface ActContent {
  id: ActId
  number: string
  filmLabel: LocalizedText
  title: LocalizedText
  body: LocalizedText
}

export interface WorldContent {
  id: 'real-face' | 'stylized-identity' | 'subject-visual'
  number: string
  name: LocalizedText
  line: LocalizedText
  imageUrl: string
  mobileImageUrl: string
}

export interface TrustBeatContent {
  id: 'consent' | 'control' | 'language'
  title: LocalizedText
  body: LocalizedText
}

export const ACT_CONTENT = [
  {
    id: 'act-00',
    number: '00',
    filmLabel: { en: 'Expert authority', ar: 'سلطة الخبير' },
    title: {
      en: 'Your professional presence\nis one approval away.',
      ar: 'حضورك المهني على مواقع التواصل\nعلى بُعد موافقة واحدة.',
    },
    body: {
      en: 'A source-supported content platform built for experts and professionals.\n\nWe research the topic, verify the information through multiple review layers, and send you the script with its source links for free expert review — before any video is generated and before you pay anything.',
      ar: 'منصة لصناعة محتوى موثّق، مصممة للمختصين والمحترفين.\n\nنبحث الموضوع، وندقق المعلومات عبر عدة طبقات، ثم نرسل لك النص وروابط المصادر لتراجعه وتوافق عليه بصفتك الخبير — مجاناً قبل توليد الفيديو وقبل دفع أي مبلغ.',
    },
  },
  {
    id: 'act-01',
    number: '01',
    filmLabel: { en: 'Verification', ar: 'التحقق' },
    title: {
      en: 'Research with sources, not guesses.',
      ar: 'بحث وتحقق متعدد الطبقات.',
    },
    body: {
      en: 'Multi-layer research and verification, integrated with Perplexity for information and source validation.',
      ar: 'بحث وتحقق متعدد الطبقات، مع تكامل Perplexity للبحث والتحقق من المعلومات والمصادر العلمية والمهنية.',
    },
  },
  {
    id: 'act-02',
    number: '02',
    filmLabel: { en: 'Script + sources', ar: 'النص والمصادر' },
    title: { en: 'The script arrives with its evidence.', ar: 'النص يصل ومعه روابط المصادر.' },
    body: {
      en: 'We prepare the script and send source links so you can inspect the claim, the context, and the wording as the expert.',
      ar: 'نبحث الموضوع، وندقق المعلومات عبر عدة طبقات، ثم نرسل لك النص وروابط المصادر لتراجعه وتوافق عليه بصفتك الخبير.',
    },
  },
  {
    id: 'act-03',
    number: '03',
    filmLabel: { en: 'Free review', ar: 'مراجعة مجانية' },
    title: {
      en: 'Review before generation or payment.',
      ar: 'راجع قبل التوليد وقبل الدفع.',
    },
    body: {
      en: 'Review the script and its sources for free before generation or payment. You are the expert. You remain the final authority.',
      ar: 'راجع النص وروابط المصادر مجاناً قبل أن نولد الفيديو وقبل أن تدفع أي مبلغ. أنت الخبير. وأنت صاحب القرار النهائي.',
    },
  },
  {
    id: 'act-04',
    number: '04',
    filmLabel: { en: 'Professional worlds', ar: 'مسارات مهنية' },
    title: {
      en: 'Choose the visual route your expertise needs.',
      ar: 'اختر المسار البصري الذي يليق بخبرتك.',
    },
    body: {
      en: 'Choose from professional content worlds: a real on-camera presence, a stylized expert identity, or subject-led visuals for complex ideas.',
      ar: 'مسارات مهنية متعددة: حضور بوجه حقيقي، أو هوية مبتكرة، أو عوالم بصرية تقود الأفكار المعقدة.',
    },
  },
  {
    id: 'act-05',
    number: '05',
    filmLabel: { en: 'Approval', ar: 'الموافقة' },
    title: {
      en: 'Approve once. Receive a publish-ready video.',
      ar: 'موافقة واحدة، وفيديو جاهز للنشر.',
    },
    body: {
      en: 'After your approval, scenes, voice, captions, and rhythm assemble into a professional, publish-ready video.',
      ar: 'بعد موافقتك، تتجمع المشاهد والصوت والنص والإيقاع في فيديو احترافي جاهز للنشر.',
    },
  },
  {
    id: 'act-06',
    number: '06',
    filmLabel: { en: 'Delivery', ar: 'التسليم والنشر' },
    title: {
      en: 'Delivered to you, or published with permission.',
      ar: 'نرسله لك، أو ننشره بإذنك.',
    },
    body: {
      en: 'Receive the finished video ready to publish, or authorize the platform to publish it for you.',
      ar: 'نرسل لك الفيديو جاهزاً للنشر، أو ننشره على صفحتك بإذنك.',
    },
  },
  {
    id: 'act-07',
    number: '07',
    filmLabel: { en: 'Community', ar: 'إدارة الصفحة' },
    title: {
      en: 'Your page can keep answering responsibly.',
      ar: 'صفحتك تستمر بحضور مسؤول.',
    },
    body: {
      en: 'Optional page management, comment replies, and source-link sharing with engaged viewers.',
      ar: 'خيارات اختيارية لإدارة صفحتك، والرد على التعليقات، وإرسال روابط المصادر للمتفاعلين المهتمين.',
    },
  },
  {
    id: 'act-08',
    number: '08',
    filmLabel: { en: 'Final authority', ar: 'القرار النهائي' },
    title: {
      en: 'Content worthy of your expertise.',
      ar: 'محتوى يليق بخبرتك.',
    },
    body: {
      en: 'Content worthy of your expertise.\nSources you can inspect.\nYou remain in control.\n\nReview the script and sources for free before generation or payment.',
      ar: 'محتوى يليق بخبرتك.\nمصادر يمكنك مراجعتها.\nوأنت صاحب القرار دائماً.\n\nراجع النص والمصادر مجاناً قبل التوليد وقبل الدفع.',
    },
  },
] as const satisfies readonly ActContent[]

export const WORLD_CONTENT = [
  {
    id: 'real-face',
    number: '01',
    name: { en: 'Real Face', ar: 'وجه حقيقي' },
    line: {
      en: 'Your face, ready for the scene.',
      ar: 'وجهك، جاهز للمشهد.',
    },
    imageUrl: '/media/images/source-portrait.webp',
    mobileImageUrl: '/media/images/source-portrait-mobile.webp',
  },
  {
    id: 'stylized-identity',
    number: '02',
    name: { en: 'Stylized Identity', ar: 'هوية مبتكرة' },
    line: {
      en: 'Design a persona beyond the camera.',
      ar: 'صمّم شخصية تتجاوز حدود الكاميرا.',
    },
    imageUrl: '/media/images/stylized-identity.webp',
    mobileImageUrl: '/media/images/stylized-identity-mobile.webp',
  },
  {
    id: 'subject-visual',
    number: '03',
    name: { en: 'Subject Visual', ar: 'الموضوع البصري' },
    line: {
      en: 'Let objects and ideas speak.',
      ar: 'دع الأشياء والأفكار تتحدث.',
    },
    imageUrl: '/media/images/subject-visual.webp',
    mobileImageUrl: '/media/images/subject-visual-mobile.webp',
  },
] as const satisfies readonly WorldContent[]

export const TRUST_BEATS = [
  {
    id: 'consent',
    title: { en: 'Free review', ar: 'مراجعة مجانية' },
    body: {
      en: 'Inspect the script and sources before generation or payment.',
      ar: 'راجع النص وروابط المصادر مجاناً قبل التوليد وقبل الدفع.',
    },
  },
  {
    id: 'control',
    title: { en: 'Final authority', ar: 'القرار النهائي' },
    body: {
      en: 'You are the expert; approval stays with you.',
      ar: 'أنت الخبير، وأنت صاحب القرار النهائي.',
    },
  },
  {
    id: 'language',
    title: { en: 'Source sharing', ar: 'مشاركة المصادر' },
    body: {
      en: 'Share source links with engaged viewers when useful.',
      ar: 'أرسل روابط المصادر للمتفاعلين المهتمين عند الحاجة.',
    },
  },
] as const satisfies readonly TrustBeatContent[]

export const UI_CONTENT = {
  skip: {
    en: 'Skip to the cinematic experience',
    ar: 'انتقل إلى التجربة السينمائية',
  },
  brandLabel: {
    en: 'Avatar Platform — return to first contact',
    ar: 'منصة أفاتار — العودة إلى الاتصال الأول',
  },
  languageLabel: { en: 'Choose language', ar: 'اختر اللغة' },
  english: { en: 'English', ar: 'الإنجليزية' },
  arabic: { en: 'Arabic', ar: 'العربية' },
  progressLabel: {
    en: 'Experience progress',
    ar: 'مراحل التجربة',
  },
  demoControls: {
    en: 'Cinematic demo controls',
    ar: 'أدوات العرض السينمائي',
  },
  demoStart: { en: 'Start demo', ar: 'ابدأ العرض' },
  demoPause: { en: 'Pause demo', ar: 'أوقف العرض مؤقتاً' },
  demoResume: { en: 'Resume demo', ar: 'تابع العرض' },
  demoRestart: { en: 'Restart', ar: 'أعد العرض' },
  demoOff: { en: 'Demo mode is off.', ar: 'وضع العرض غير مفعّل.' },
  demoReady: { en: 'Demo is ready.', ar: 'العرض جاهز.' },
  demoPlaying: { en: 'Demo is playing.', ar: 'العرض قيد التشغيل.' },
  demoPaused: { en: 'Demo is paused.', ar: 'العرض متوقف مؤقتاً.' },
  verticalCapture: {
    en: 'Vertical capture framing is active.',
    ar: 'تأطير التسجيل العمودي مفعّل.',
  },
  captureKeyboardHelp: {
    en: 'Capture controls: press P to pause or resume, R to restart, and Escape to stop autoplay.',
    ar: 'أدوات التسجيل: اضغط P للإيقاف أو المتابعة، وR لإعادة التشغيل، وEscape لإيقاف العرض التلقائي.',
  },
  worldSequence: {
    en: 'Three creation worlds',
    ar: 'عوالم الإبداع الثلاثة',
  },
  reelFigure: {
    en: 'Three source plates converge into an assembled vertical reel.',
    ar: 'تلتقي ثلاث لوحات مصدر لتكوّن ريل عمودياً مكتملًا.',
  },
  trustSequence: {
    en: 'Expert authority workflow',
    ar: 'مسار سلطة الخبير',
  },
  primaryCta: { en: 'Review script + sources', ar: 'راجع النص والمصادر' },
  primaryCtaLabel: {
    en: 'Review the script and sources for free before generation or payment',
    ar: 'راجع النص والمصادر مجاناً قبل التوليد وقبل الدفع',
  },
} as const satisfies Record<string, LocalizedText>

export function localize(text: LocalizedText, locale: Locale): string {
  return text[locale]
}
